import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import API_ENDPOINTS from "../ApiEndPoints";

// Small helper to debounce a function
function useDebouncedCallback(callback, delay) {
  const timeoutRef = useRef(null);
  return (...args) => {
    window.clearTimeout(timeoutRef.current);
    timeoutRef.current = window.setTimeout(() => callback(...args), delay);
  };
}

const initialState = {
  name: "",
  email: "",
  email_verified: true,
  smsNumber: "",
  dob: "",
  dsgh: 0,
  photo: "",
  preferences: {
    email_notifications: false,
    email_daily_task_summaries: false,
    // use_cb_webeditor optional, hidden in UI
  },
  created_at: "",
  updated_at: "",
  badges: [],
  points: 0,
};

export default function UserProfile() {
  const [data, setData] = useState(initialState);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [unsaved, setUnsaved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get(API_ENDPOINTS.me.get);
        if (!mounted) return;
        setData((d) => ({ ...d, ...res.data }));
      } catch (e) {
        setError("Failed to load profile");
        // eslint-disable-next-line no-console
        console.error(e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const debouncedSave = useDebouncedCallback(async (nextData) => {
    try {
      setSaving(true);
      await axios.post(API_ENDPOINTS.me.save, {
        name: nextData.name,
        email: nextData.email,
        smsNumber: nextData.smsNumber,
        dob: nextData.dob || null,
        preferences: {
          email_notifications: !!nextData.preferences.email_notifications,
          email_daily_task_summaries: !!nextData.preferences
            .email_daily_task_summaries,
        },
      });
      setUnsaved(false);
    } catch (e) {
      setError("Failed to save changes");
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setSaving(false);
    }
  }, 600);

  const onChange = (patch) => {
    const next = { ...data, ...patch };
    setData(next);
    setUnsaved(true);
    debouncedSave(next);
  };

  const onPrefChange = (key, value) => {
    const next = {
      ...data,
      preferences: { ...data.preferences, [key]: value },
    };
    setData(next);
    setUnsaved(true);
    debouncedSave(next);
  };

  const sendVerification = async () => {
    try {
      await axios.post(API_ENDPOINTS.me.sendVerification);
      alert("Verification email sent");
    } catch (e) {
      alert("Verification email failed to send");
    }
  };

  // Removed: clearDontShowModals UI and call per request

  const onFileSelected = async (file) => {
    if (!file) return;
    try {
      setUploading(true);
      // 1) presign
      const presign = await axios.post(API_ENDPOINTS.me.avatarPresign, {
        filename: file.name,
        size: file.size,
        mimetype: file.type,
      });
      const { key, presigned_put, storage_drive } = presign.data;
      // 2) PUT to S3
      await fetch(presigned_put, {
        method: "PUT",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });
      // 3) complete
      const done = await axios.post(API_ENDPOINTS.me.avatarComplete, {
        key,
        storage_drive,
      });
      const photoUrl = done.data.photo;
      setData((d) => ({ ...d, photo: photoUrl }));
      // Save profile to persist updated_at etc (optional)
      debouncedSave({ ...data, photo: photoUrl });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      alert("Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="container-fluid p-0">
      {!data.email_verified && (
        <div className="alert alert-danger">
          Your email is not verified. Please verify your email address to unlock the system fully.
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              sendVerification();
            }}
            className="m-l-10"
          >
            Send Verification Email
          </a>
        </div>
      )}

      {unsaved && (
        <div className="alert alert-info m-b-10">
          <strong>Changes not save!</strong>
        </div>
      )}

      <div className="row">
        {/* Sidebar: Photo, Points, Badges */}
        <div className="col-12 col-md-4 col-lg-3 m-b-15">
          <div className="do-field-margin">
            <label className="field">Photo</label>
            <div className="upload-image text-center">
              {data.photo ? (
                <img
                  src={data.photo}
                  alt="User avatar"
                  className="img-thumbnail img-fluid w-100 m-b-10"
                />
              ) : (
                <div className="text-muted m-b-10">No photo</div>
              )}
              <input
                id="properties_photo"
                className="form-control"
                type="file"
                accept="image/*"
                onChange={(e) => onFileSelected(e.target.files[0])}
                style={{ display: "none" }}
              />
              <label
                htmlFor="properties_photo"
                style={{ verticalAlign: "bottom" }}
                className="btn btn-sm btn-primary"
              >
                {uploading ? "Uploading..." : "Change"}
              </label>
            </div>
          </div>

          <div className="m-t-15">
            <h5 className="m-b-10">Your Points</h5>
            <div className="form-control text-center">{data.points} DOY points</div>
          </div>

          <div className="m-t-15">
            <h5 className="m-b-10">Badges</h5>
            <div className="form-control-sm">
              {(data.badges || []).length === 0 && (
                <span className="text-muted">No badges yet</span>
              )}
              {(data.badges || []).map((b) => (
                <span key={b} className="label label-purple m-r-5 m-b-5 d-inline-block">
                  {b}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Main Form */}
        <div className="col-12 col-md-8 col-lg-9">
          <form onSubmit={(e) => e.preventDefault()}>
            <div className="row">
              <div className="col-12 col-md-6">
                <div className="do-field-margin">
                  <label htmlFor="name" className="field">
                    Name<span className="text-danger">*</span>
                  </label>
                  <input
                    id="name"
                    className="form-control m-t-10 m-b-10"
                    type="text"
                    value={data.name || ""}
                    onChange={(e) => onChange({ name: e.target.value })}
                  />
                </div>
              </div>

              <div className="col-12 col-md-6">
                <label htmlFor="email" className="field">
                  Email<span className="text-danger">*</span>
                </label>
                <input
                  id="email"
                  className="form-control m-t-10 m-b-10"
                  type="email"
                  value={data.email || ""}
                  onChange={(e) => onChange({ email: e.target.value })}
                />
                <small className="text-muted">
                  Changing your email requires re-verification before you can log back in.
                </small>
                {!data.email_verified && (
                  <div className="m-t-10">
                    <button
                      className="btn btn-success btn-sm"
                      onClick={(e) => {
                        e.preventDefault();
                        sendVerification();
                      }}
                    >
                      Send Verification Email
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-12 col-md-6">
                <div className="do-field-margin">
                  <label htmlFor="smsNumber" className="field">SMS Number</label>
                  <input
                    id="smsNumber"
                    className="form-control m-t-10 m-b-10"
                    type="text"
                    value={data.smsNumber || ""}
                    onChange={(e) => onChange({ smsNumber: e.target.value })}
                  />
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="do-field-margin">
                  <label htmlFor="dob" className="field">Date of Birth</label>
                  <input
                    id="dob"
                    className="form-control m-t-10 m-b-10"
                    type="date"
                    value={data.dob || ""}
                    onChange={(e) => onChange({ dob: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <h4 className="m-t-15">ACCOUNT PREFERENCES</h4>
            <div className="row">
              <div className="col-12 col-md-6">
                <div className="form-check m-t-10 m-b-10">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="email_notifications"
                    checked={!!data.preferences.email_notifications}
                    onChange={(e) => onPrefChange("email_notifications", e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="email_notifications">
                    Send Email Notifications
                  </label>
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="form-check m-t-10 m-b-10">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="email_daily_task_summaries"
                    checked={!!data.preferences.email_daily_task_summaries}
                    onChange={(e) => onPrefChange("email_daily_task_summaries", e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="email_daily_task_summaries">
                    Email daily task summaries
                  </label>
                </div>
              </div>
            </div>

            <div className="row m-t-10">
              <div className="col-12 col-md-6">
                <label className="field">Created</label>
                <div className="form-control" id="created_at">{data.created_at}</div>
              </div>
              <div className="col-12 col-md-6">
                <label className="field">Updated</label>
                <div className="form-control" id="updated_at">{data.updated_at}</div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
